import * as React from "react";
import {
    ColumnDef,
    ColumnFiltersState,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    Row,
} from "@tanstack/react-table";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useEffect } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { PaginationControls } from "@/components/pagination-controls/pagination-controls";

export type Payment = {
    id: string;
    amount: number;
    status: "pending" | "processing" | "success" | "failed";
    email: string;
};

type ColumnMetaType = {
    align?: "left" | "right" | "center";
};

interface CardsDataTableProps<T> {
    title: string;
    description: string;
    columns: ColumnDef<T, unknown>[];
    data: T[];
    resourceKey?: string;
    onRowClick?: (event: React.MouseEvent<HTMLTableRowElement>, row: T) => void;
    onSelectionChange?: (rows: T[]) => void;
    // Pagination props
    totalCount?: number;
    currentPage?: number;
    pageSize?: number;
    onPageChange?: (page: number) => void;
    onPageReset?: () => void;
    // Loading state
    isLoading?: boolean;

    /** Optional actions to display in the table header */
    actions?: React.ReactNode;

    /** Optional search placeholder */
    search?: string;
    onSearchChange?: (search: string) => void;

    /** Optional sorting state */
    sorting?: SortingState;
    onSortingChange?: (sorting: SortingState) => void;
}

export function CardsDataTable<T>({
    title,
    description,
    columns,
    resourceKey,
    data,
    onRowClick,
    onSelectionChange,
    totalCount = 0,
    currentPage = 0,
    pageSize = 10,
    onPageChange,
    onPageReset,
    isLoading,
    actions,
    search,
    onSearchChange,
    sorting,
    onSortingChange,
}: CardsDataTableProps<T>) {
    const { t } = useTranslation();
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
    const [rowSelection, setRowSelection] = React.useState({});

    const table = useReactTable({
        data,
        columns,
        onSortingChange: (updater) => {
            if (onSortingChange) {
                const newSorting = updater instanceof Function ? updater(sorting || []) : updater;
                onSortingChange(newSorting);
                // Reset page when sorting changes
                if (onPageReset) {
                    onPageReset();
                }
            }
        },
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: (updater) => {
            const newSelection = updater instanceof Function ? updater(rowSelection) : updater;
            setRowSelection(newSelection);
            if (onSelectionChange) {
                const selectedRows = Object.keys(newSelection)
                    .map((key) => data[parseInt(key, 10)])
                    .filter((row): row is NonNullable<typeof row> => row !== undefined);
                onSelectionChange(selectedRows);
            }
        },
        state: {
            sorting: sorting || [],
            columnFilters,
            columnVisibility,
            rowSelection,
            pagination: {
                pageIndex: currentPage,
                pageSize: pageSize,
            },
        },
        manualPagination: true,
        pageCount: Math.ceil(totalCount / pageSize),
        enableMultiRowSelection: true,
    });

    // Reset row selection when data changes
    useEffect(() => {
        setRowSelection({});
    }, [data]);

    const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>, row: Row<T>) => {
        if (onRowClick) {
            onRowClick(event, row.original);
        }
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <Card className="flex flex-col w-full h-full">
            <CardHeader className="flex flex-row justify-between flex-shrink-0">
                <div className="flex flex-col gap-1">
                    <CardTitle className="text-xl">{title}</CardTitle>
                    <CardDescription>{description}</CardDescription>
                </div>
                {actions && <div className="flex items-center gap-2">{actions}</div>}
            </CardHeader>
            <CardContent className="flex-1 w-full overflow-hidden flex flex-col">
                <div className="mb-4 flex items-center gap-4 flex-shrink-0">
                    <Input
                        placeholder={
                            resourceKey
                                ? t(`screens.${resourceKey}.searchPlaceholder`)
                                : t("general.searchPlaceholder")
                        }
                        className="max-w-sm"
                        value={search}
                        onChange={(e) => onSearchChange?.(e.target.value)}
                    />
                </div>
                {/* Wrapper for horizontal scrolling */}
                <div className="flex-1 overflow-hidden">
                    <div className="overflow-x-auto h-full rounded-lg border border-border/60">
                        <Table className="whitespace-nowrap">
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead
                                                key={header.id}
                                                className={cn(
                                                    "[&:has([role=checkbox])]:pl-3",
                                                    header.column.getCanSort() &&
                                                        "cursor-pointer select-none",
                                                    "px-4",
                                                )}
                                                style={{ width: `${header.column.getSize()}rem` }}
                                                onClick={header.column.getToggleSortingHandler()}
                                            >
                                                <div
                                                    className={cn(
                                                        "flex items-center gap-2",
                                                        (
                                                            header.column.columnDef
                                                                .meta as ColumnMetaType
                                                        )?.align === "right"
                                                            ? "justify-end"
                                                            : "justify-start",
                                                    )}
                                                >
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column.columnDef.header,
                                                              header.getContext(),
                                                          )}
                                                    {header.column.getCanSort() && (
                                                        <>
                                                            {header.column.getIsSorted() ===
                                                            "desc" ? (
                                                                <ArrowDown className="h-3 w-3 text-foreground" />
                                                            ) : header.column.getIsSorted() ===
                                                              "asc" ? (
                                                                <ArrowUp className="h-3 w-3 text-foreground" />
                                                            ) : (
                                                                <ChevronsUpDown className="h-3 w-3 text-muted-foreground" />
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={columns.length}
                                            className="h-24 text-center"
                                        >
                                            <p className="text-sm text-muted-foreground">
                                                {t("general.loading")}
                                            </p>
                                        </TableCell>
                                    </TableRow>
                                ) : table.getRowModel().rows?.length ? (
                                    table.getRowModel().rows.map((row) => (
                                        <TableRow
                                            key={row.id}
                                            data-state={row.getIsSelected() && "selected"}
                                            onClick={(event) => handleRowClick(event, row)}
                                            className={cn(
                                                onRowClick && "cursor-pointer hover:bg-muted/50",
                                            )}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell
                                                    key={cell.id}
                                                    className={cn(
                                                        "[&:has([role=checkbox])]:pl-3 px-4",
                                                    )}
                                                    style={{
                                                        width: `${cell.column.getSize()}rem`,
                                                    }}
                                                >
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext(),
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell
                                            colSpan={columns.length}
                                            className="h-24 text-center"
                                        >
                                            <p className="text-sm text-muted-foreground">
                                                {t("general.noResults")}
                                            </p>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>

                {/* Updated pagination section */}
                <div className="flex items-center justify-between pt-4 flex-shrink-0 mt-4">
                    <div className="flex-1 text-sm text-muted-foreground">
                        {t("general.table.rowsSelected", {
                            count: Object.keys(rowSelection).length,
                        })}
                    </div>
                    <div className="flex-1 flex justify-center">
                        <PaginationControls
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={onPageChange || (() => {})}
                        />
                    </div>
                    <div className="flex-1" /> {/* Spacer for balanced layout */}
                </div>
            </CardContent>
        </Card>
    );
}
